import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { researchApi } from "../../../services/researchApi";
import {
  Criterion,
  FuzzyRubric,
  Requirements,
} from "../../../types/researchWorkflow";
import GenerateRequirementsModal from "./GenerateRequirementsModal";
import VersionHistoryModal from "./VersionHistoryModal";

const SCORE_LABELS: Record<string, string> = {
  "1": "Very Poor",
  "2": "Poor",
  "3": "Adequate",
  "4": "Good",
  "5": "Excellent",
};

const DEFAULT_RUBRIC: FuzzyRubric = {
  anchors: { "1": "", "2": "", "3": "", "4": "", "5": "" },
  scale_min: 1,
  scale_max: 5,
};

interface RequirementsEditorProps {
  requirements: Requirements;
  onUpdateObjective: (objective: string) => void;
  onAddCriterion: (criterion: Criterion) => void;
  onUpdateCriterion: (criterionId: string, updates: Partial<Criterion>) => void;
  onRemoveCriterion: (criterionId: string) => void;
  isEditMode?: boolean;
  onToggleEditMode?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  trackId?: string;
  specVersionId?: string;
  onRequirementsGenerated?: (runSummary: any) => void;
  isViewer?: boolean;
}

const RequirementsEditor: React.FC<RequirementsEditorProps> = ({
  requirements,
  onUpdateObjective,
  onAddCriterion,
  onUpdateCriterion,
  onRemoveCriterion,
  isEditMode = false,
  onToggleEditMode,
  onSave,
  onCancel,
  isSaving = false,
  trackId,
  specVersionId,
  onRequirementsGenerated,
  isViewer = false,
}) => {
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  // A completed AI generation run whose proposal has not been reviewed yet.
  // The live path (modal polling) only applies the proposal while the modal is
  // mounted; if the user navigated away before the run finished, the result
  // sits in run_summary with nothing surfacing it — so on view-mode mount we
  // look it up and offer a Review/Dismiss banner. "Reviewed" is remembered per
  // track in localStorage so the banner does not resurface for the same run.
  const [pendingGeneration, setPendingGeneration] = useState<any | null>(null);
  const reviewedKey = trackId ? `reqgen-reviewed:${trackId}` : null;
  const markGenerationReviewed = useCallback(
    (runId: string | undefined) => {
      if (!reviewedKey || !runId) return;
      try {
        window.localStorage.setItem(reviewedKey, runId);
      } catch {
        /* storage unavailable — banner may show again, harmless */
      }
      setPendingGeneration(null);
    },
    [reviewedKey],
  );
  const handleGenerated = useCallback(
    (runSummary: any, runId?: string) => {
      markGenerationReviewed(runId);
      onRequirementsGenerated?.(runSummary);
    },
    [markGenerationReviewed, onRequirementsGenerated],
  );

  useEffect(() => {
    if (!trackId || !reviewedKey || !onRequirementsGenerated || isEditMode) {
      return;
    }
    let cancelled = false;
    researchApi
      .getLatestRequirementsGenerationRun(trackId)
      .then((run) => {
        if (cancelled || !run) return;
        let reviewed: string | null = null;
        try {
          reviewed = window.localStorage.getItem(reviewedKey);
        } catch {
          reviewed = null;
        }
        if (reviewed !== run.id) setPendingGeneration(run);
      })
      .catch((err) => {
        console.warn("Could not check for generated requirements:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [trackId, reviewedKey, onRequirementsGenerated, isEditMode]);
  const invalidCriteria = requirements.criteria.filter(
    (c) => !c.label.trim() || !c.value.trim(),
  );
  const criteriaEndRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);

  useEffect(() => {
    if (shouldScrollRef.current) {
      shouldScrollRef.current = false;
      criteriaEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [requirements.criteria.length]);

  const handleAddCriterion = () => {
    const newCriterion: Criterion = {
      id: `crit-${Date.now()}`,
      label: "",
      value: "",
      weight: 3,
      mode: "fuzzy",
      rubric: { ...DEFAULT_RUBRIC, anchors: { ...DEFAULT_RUBRIC.anchors } },
    };
    shouldScrollRef.current = true;
    onAddCriterion(newCriterion);
  };

  const handleRubricAnchorChange = (
    criterionId: string,
    currentRubric: FuzzyRubric | undefined,
    scoreKey: string,
    value: string,
  ) => {
    const rubric = currentRubric ?? {
      ...DEFAULT_RUBRIC,
      anchors: { ...DEFAULT_RUBRIC.anchors },
    };
    onUpdateCriterion(criterionId, {
      rubric: {
        ...rubric,
        anchors: { ...rubric.anchors, [scoreKey]: value },
      },
    });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Proposal from a generation run that finished while the user was away */}
      {!isViewer && !isEditMode && pendingGeneration && (
        <Alert
          severity="info"
          icon={<AutoAwesomeIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
          action={
            <>
              <Button
                color="inherit"
                size="small"
                onClick={() =>
                  handleGenerated(
                    pendingGeneration.run_summary,
                    pendingGeneration.id,
                  )
                }
              >
                Review
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => markGenerationReviewed(pendingGeneration.id)}
              >
                Dismiss
              </Button>
            </>
          }
        >
          AI-generated requirements
          {pendingGeneration.finished_at
            ? ` from ${new Date(pendingGeneration.finished_at).toLocaleString()}`
            : ""}{" "}
          are ready to review. Reviewing opens them in the editor; nothing is
          saved until you click Save.
        </Alert>
      )}
      {/* Header with Edit/Save/Cancel buttons */}
      {!isViewer && (
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 1 }}
        >
          {!isEditMode ? (
            <>
              {trackId && (
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryModalOpen(true)}
                >
                  Version History
                </Button>
              )}
              {trackId && onRequirementsGenerated && (
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setGenerateModalOpen(true)}
                  color="primary"
                >
                  Generate Requirements
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={onToggleEditMode}
              >
                Edit Requirements
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={onSave}
                disabled={isSaving || invalidCriteria.length > 0}
                title={
                  invalidCriteria.length > 0
                    ? `Missing label or description: ${invalidCriteria.map((c) => c.label || "Untitled").join(", ")}`
                    : undefined
                }
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Objective Section */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, border: 1, borderColor: "divider" }}
      >
        <Typography variant="h6" gutterBottom fontWeight="600">
          Research Objective
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define the primary goal of this research workflow
        </Typography>
        {isEditMode ? (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={requirements.objective}
            onChange={(e) => onUpdateObjective(e.target.value)}
            placeholder="e.g., Identify strategic partners for next-generation defense systems development and production"
            variant="outlined"
          />
        ) : (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
            }}
          >
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
            >
              {requirements.objective || (
                <span style={{ color: "text.disabled", fontStyle: "italic" }}>
                  No objective defined yet
                </span>
              )}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Criteria Section */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, border: 1, borderColor: "divider" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="600">
              Evaluation Criteria
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define the criteria for evaluating candidate entities
            </Typography>
          </Box>
          {isEditMode && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCriterion}
              size="small"
            >
              Add Criterion
            </Button>
          )}
        </Box>

        {requirements.criteria.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              color: "text.secondary",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              borderStyle: "dashed",
            }}
          >
            <Typography variant="body2">
              No criteria defined yet. Click "Add Criterion" to get started.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {requirements.criteria.map((criterion, index) => (
              <Paper
                key={criterion.id}
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: "#1e1e1e",
                }}
              >
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  {/* Criterion Number - only show in edit mode */}
                  {isEditMode && (
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        flexShrink: 0,
                        mt: 1,
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}

                  {/* Criterion Fields */}
                  {isEditMode ? (
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                        }}
                      >
                        {/* Left side: Label and Weight/Mode in column */}
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <TextField
                            label="Criterion Label"
                            value={criterion.label}
                            onChange={(e) =>
                              onUpdateCriterion(criterion.id, {
                                label: e.target.value,
                              })
                            }
                            placeholder="e.g., Technical Capability"
                            size="small"
                            multiline
                            minRows={1}
                          />

                          {/* Weight */}
                          <FormControl size="small" fullWidth>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              Weight (Importance)
                            </Typography>
                            <Select
                              value={criterion.weight}
                              onChange={(e) =>
                                onUpdateCriterion(criterion.id, {
                                  weight: e.target.value as 0 | 1 | 2 | 3 | 4 | 5,
                                })
                              }
                            >
                              <MenuItem value={0}>0 - Exclude from score</MenuItem>
                              <MenuItem value={1}>1 - Low</MenuItem>
                              <MenuItem value={2}>2 - Below Average</MenuItem>
                              <MenuItem value={3}>3 - Average</MenuItem>
                              <MenuItem value={4}>4 - High</MenuItem>
                              <MenuItem value={5}>5 - Critical</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Right side: Value field spanning full height */}
                        <TextField
                          label="Criterion Value/Description"
                          value={criterion.value}
                          onChange={(e) =>
                            onUpdateCriterion(criterion.id, {
                              value: e.target.value,
                            })
                          }
                          placeholder="e.g., Advanced aerospace and defense technology expertise"
                          size="small"
                          multiline
                          minRows={4}
                          sx={{
                            flex: 2,
                            "& .MuiInputBase-root": {
                              height: "100%",
                              alignItems: "flex-start",
                            },
                          }}
                        />
                      </Box>

                      {/* Rubric Editor (edit mode) */}
                      {criterion.mode === "fuzzy" && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                            backgroundColor: "rgba(255,255,255,0.02)",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ mb: 1.5 }}
                          >
                            Scoring Rubric — define what each score level means
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                            }}
                          >
                            {["5", "4", "3", "2", "1"].map((scoreKey) => (
                              <Box
                                key={scoreKey}
                                sx={{
                                  display: "flex",
                                  gap: 1.5,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Chip
                                  label={`${scoreKey} — ${SCORE_LABELS[scoreKey]}`}
                                  size="small"
                                  sx={{
                                    mt: 0.75,
                                    minWidth: 120,
                                    fontWeight: 600,
                                    backgroundColor:
                                      scoreKey === "5"
                                        ? "success.dark"
                                        : scoreKey === "4"
                                          ? "success.main"
                                          : scoreKey === "3"
                                            ? "warning.main"
                                            : scoreKey === "2"
                                              ? "error.main"
                                              : "error.dark",
                                    color: "white",
                                  }}
                                />
                                <TextField
                                  fullWidth
                                  size="small"
                                  multiline
                                  minRows={1}
                                  maxRows={3}
                                  placeholder={`Describe what a score of ${scoreKey} (${SCORE_LABELS[scoreKey]}) looks like...`}
                                  value={
                                    criterion.rubric?.anchors?.[scoreKey] ?? ""
                                  }
                                  onChange={(e) =>
                                    handleRubricAnchorChange(
                                      criterion.id,
                                      criterion.rubric,
                                      scoreKey,
                                      e.target.value,
                                    )
                                  }
                                />
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 1.5,
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6" fontWeight="600">
                          {criterion.label || "Untitled Criterion"}
                        </Typography>
                        <Chip
                          label={
                            criterion.weight === 0
                              ? "Excluded from score"
                              : `Weight ${criterion.weight}/5`
                          }
                          size="small"
                          sx={{
                            ml: "auto",
                            fontWeight: 600,
                            backgroundColor:
                              criterion.weight === 5
                                ? "primary.dark"
                                : criterion.weight === 4
                                  ? "primary.main"
                                  : criterion.weight === 3
                                    ? "primary.light"
                                    : criterion.weight === 2
                                      ? "grey.600"
                                      : criterion.weight === 1
                                        ? "grey.700"
                                        : "grey.400",
                            color: "common.black",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
                      >
                        {criterion.value || (
                          <span
                            style={{
                              color: "text.disabled",
                              fontStyle: "italic",
                            }}
                          >
                            No description provided
                          </span>
                        )}
                      </Typography>

                      {/* Rubric Accordion (read mode) */}
                      {criterion.mode === "fuzzy" && (
                        <Accordion
                          disableGutters
                          elevation={0}
                          sx={{
                            mt: 1.5,
                            backgroundColor: "transparent",
                            border: 1,
                            borderColor: "divider",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: 36, px: 1.5 }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                            >
                              Scoring Rubric
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {["5", "4", "3", "2", "1"].map((scoreKey) => (
                                <Box
                                  key={scoreKey}
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Chip
                                    label={`${scoreKey}`}
                                    size="small"
                                    sx={{
                                      mt: 0.25,
                                      minWidth: 28,
                                      fontWeight: 600,
                                      backgroundColor:
                                        scoreKey === "5"
                                          ? "success.dark"
                                          : scoreKey === "4"
                                            ? "success.main"
                                            : scoreKey === "3"
                                              ? "warning.main"
                                              : scoreKey === "2"
                                                ? "error.main"
                                                : "error.dark",
                                      color: "white",
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      lineHeight: 1.5,
                                      color: criterion.rubric?.anchors?.[
                                        scoreKey
                                      ]?.trim()
                                        ? "text.secondary"
                                        : "text.disabled",
                                      fontStyle: criterion.rubric?.anchors?.[
                                        scoreKey
                                      ]?.trim()
                                        ? "normal"
                                        : "italic",
                                    }}
                                  >
                                    {criterion.rubric?.anchors?.[
                                      scoreKey
                                    ]?.trim() ||
                                      `${SCORE_LABELS[scoreKey]} (not defined)`}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Box>
                  )}

                  {/* Delete Button */}
                  {isEditMode && (
                    <IconButton
                      onClick={() => onRemoveCriterion(criterion.id)}
                      size="small"
                      color="error"
                      sx={{ mt: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ))}
            {isEditMode && (
              <Box
                onClick={handleAddCriterion}
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderStyle: "dashed",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  cursor: "pointer",
                  color: "text.secondary",
                  "&:hover": {
                    borderColor: "primary.main",
                    color: "primary.main",
                  },
                }}
              >
                <AddIcon fontSize="small" />
                <Typography variant="body2" fontWeight={600}>
                  ADD CRITERION
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Help Text */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          backgroundColor: "rgba(33, 150, 243, 0.1)",
          border: 1,
          borderColor: "primary.main",
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color="text.primary">
          <strong>💡 Tip:</strong> Define clear, specific criteria with
          appropriate weights to help the agent identify the most relevant
          candidates.
        </Typography>
      </Paper>
      <div ref={criteriaEndRef} />

      {/* Generate Requirements Modal */}
      {trackId && onRequirementsGenerated && (
        <GenerateRequirementsModal
          open={generateModalOpen}
          onClose={() => setGenerateModalOpen(false)}
          trackId={trackId}
          specVersionId={specVersionId}
          onRequirementsGenerated={(runSummary, runId) =>
            handleGenerated(runSummary, runId)
          }
        />
      )}

      {/* Version History Modal */}
      {trackId && (
        <VersionHistoryModal
          open={historyModalOpen}
          onClose={() => setHistoryModalOpen(false)}
          trackId={trackId}
        />
      )}
    </Box>
  );
};

export default RequirementsEditor;
